import React, { useState, useEffect, useMemo } from 'react';

const ProgressGamification: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState(5);
  const [currentXP, setCurrentXP] = useState(750);
  const [animatingBadge, setAnimatingBadge] = useState<string | null>(null);
  const [streakCount, setStreakCount] = useState(7);
  const [showLevelUp, setShowLevelUp] = useState(false);

  const achievements = useMemo(() => [
    { id: 'first-quiz', name: 'First Steps', icon: '👶', description: 'Complete your first quiz', unlocked: true, rarity: 'common' },
    { id: 'streak-7', name: 'Week Warrior', icon: '🔥', description: '7-day learning streak', unlocked: true, rarity: 'uncommon' },
    { id: 'math-master', name: 'Math Master', icon: '🧮', description: 'Score 100% in math quiz', unlocked: true, rarity: 'rare' },
    { id: 'speed-demon', name: 'Speed Demon', icon: '⚡', description: 'Complete quiz in under 5 minutes', unlocked: false, rarity: 'epic' },
    { id: 'perfectionist', name: 'Perfectionist', icon: '💎', description: 'Get 10 perfect scores', unlocked: false, rarity: 'legendary' },
    { id: 'night-owl', name: 'Night Owl', icon: '🦉', description: 'Study after midnight', unlocked: true, rarity: 'uncommon' },
    { id: 'early-bird', name: 'Early Bird', icon: '🐦', description: 'Study before 6 AM', unlocked: false, rarity: 'rare' },
    { id: 'knowledge-seeker', name: 'Knowledge Seeker', icon: '🔍', description: 'Complete 50 quizzes', unlocked: true, rarity: 'epic' }
  ], []);

  const milestones = useMemo(() => [
    { level: 1, title: 'Curious Beginner', xpRequired: 0, reward: 'Welcome Badge', unlocked: true },
    { level: 2, title: 'Eager Learner', xpRequired: 100, reward: 'Study Streak Multiplier', unlocked: true },
    { level: 3, title: 'Knowledge Seeker', xpRequired: 250, reward: 'Custom Avatar', unlocked: true },
    { level: 4, title: 'Brilliant Mind', xpRequired: 500, reward: 'Exclusive Themes', unlocked: true },
    { level: 5, title: 'Academic Star', xpRequired: 750, reward: 'AI Tutor Beta Access', unlocked: true },
    { level: 6, title: 'Scholar Elite', xpRequired: 1000, reward: 'Priority Support', unlocked: false },
    { level: 7, title: 'Wisdom Master', xpRequired: 1500, reward: 'Mentor Badge', unlocked: false },
    { level: 8, title: 'Genius Prodigy', xpRequired: 2500, reward: 'Hall of Fame', unlocked: false }
  ], []);

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#95a5a6';
      case 'uncommon': return '#27ae60';
      case 'rare': return '#3498db';
      case 'epic': return '#9b59b6';
      case 'legendary': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const getLevelProgress = () => {
    const currentMilestone = milestones.find(m => m.level === currentLevel);
    const nextMilestone = milestones.find(m => m.level === currentLevel + 1);
    
    if (!currentMilestone || !nextMilestone) return 100;
    
    const progress = ((currentXP - currentMilestone.xpRequired) / (nextMilestone.xpRequired - currentMilestone.xpRequired)) * 100;
    return Math.min(progress, 100);
  };

  const simulateXPGain = () => {
    const xpGain = Math.floor(Math.random() * 100) + 50;
    setCurrentXP(prev => {
      const newXP = prev + xpGain;
      const nextLevel = milestones.find(m => m.level === currentLevel + 1);
      
      if (nextLevel && newXP >= nextLevel.xpRequired) {
        setCurrentLevel(currentLevel + 1);
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 3000);
      }
      
      return newXP;
    });
  };

  const unlockRandomAchievement = () => {
    const lockedAchievements = achievements.filter(a => !a.unlocked);
    if (lockedAchievements.length > 0) {
      const randomAchievement = lockedAchievements[Math.floor(Math.random() * lockedAchievements.length)];
      setAnimatingBadge(randomAchievement.id);
      setTimeout(() => setAnimatingBadge(null), 2000);
    }
  };

  useEffect(() => {
    // Simulate streak updates
    const streakTimer = setInterval(() => {
      setStreakCount(prev => prev + 1);
    }, 10000);

    return () => clearInterval(streakTimer);
  }, []);

  return (
    <div className="progress-gamification">
      <style>{`
        .progress-gamification {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 20px;
          padding: 2rem;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .gamification-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 70% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .progress-header {
          text-align: center;
          margin-bottom: 2rem;
          position: relative;
          z-index: 1;
        }

        .progress-title {
          font-size: 2.2rem;
          font-weight: bold;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4, #45b7d1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.5rem;
        }

        .progress-subtitle {
          opacity: 0.9;
          font-size: 1.1rem;
        }

        .level-section {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 2rem;
          margin-bottom: 2rem;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
        }

        .level-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .level-badge {
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          padding: 1rem 2rem;
          border-radius: 50px;
          font-size: 1.5rem;
          font-weight: bold;
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
          animation: levelPulse 2s infinite;
        }

        @keyframes levelPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .xp-info {
          text-align: right;
        }

        .xp-current {
          font-size: 1.8rem;
          font-weight: bold;
          color: #feca57;
        }

        .xp-label {
          font-size: 0.9rem;
          opacity: 0.8;
        }

        .progress-bar-container {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          height: 20px;
          overflow: hidden;
          margin-bottom: 1rem;
          position: relative;
        }

        .progress-bar {
          background: linear-gradient(90deg, #ff6b9d, #4ecdc4, #45b7d1);
          height: 100%;
          border-radius: 10px;
          transition: width 1s ease;
          position: relative;
          overflow: hidden;
        }

        .progress-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: -50%;
          right: -50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .milestone-text {
          text-align: center;
          font-size: 1.1rem;
          margin-bottom: 1rem;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .action-button {
          padding: 0.8rem 1.5rem;
          border: none;
          border-radius: 25px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .action-button.xp-button {
          background: linear-gradient(45deg, #feca57, #ff9ff3);
        }

        .action-button.achievement-button {
          background: linear-gradient(45deg, #48dbfb, #0abde3);
        }

        .action-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.3);
        }

        .action-button:active {
          transform: translateY(0);
        }

        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .achievement-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 1.5rem;
          text-align: center;
          border: 2px solid transparent;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .achievement-card.unlocked {
          border-color: currentColor;
          background: rgba(255, 255, 255, 0.15);
        }

        .achievement-card.locked {
          opacity: 0.5;
          filter: grayscale(50%);
        }

        .achievement-card.animating {
          animation: achievementUnlock 2s ease;
        }

        @keyframes achievementUnlock {
          0% { transform: scale(1); }
          25% { transform: scale(1.1); box-shadow: 0 0 30px currentColor; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .achievement-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          display: block;
        }

        .achievement-name {
          font-size: 1.2rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }

        .achievement-description {
          font-size: 0.9rem;
          opacity: 0.9;
          line-height: 1.4;
        }

        .rarity-indicator {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          animation: rarityGlow 2s infinite;
        }

        @keyframes rarityGlow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; box-shadow: 0 0 10px currentColor; }
        }

        .stats-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 1.5rem;
          text-align: center;
          backdrop-filter: blur(5px);
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: 1rem;
          opacity: 0.9;
        }

        .level-up-notification {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4);
          padding: 2rem 3rem;
          border-radius: 20px;
          text-align: center;
          z-index: 1000;
          box-shadow: 0 20px 40px rgba(0,0,0,0.5);
          animation: levelUpAppear 3s ease;
        }

        @keyframes levelUpAppear {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          20% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
          80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        }

        .level-up-text {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }

        .level-up-subtitle {
          font-size: 1.2rem;
          opacity: 0.9;
        }

        @media (max-width: 768px) {
          .progress-gamification {
            padding: 1.5rem;
          }
          
          .level-info {
            flex-direction: column;
            text-align: center;
          }
          
          .xp-info {
            text-align: center;
          }
          
          .achievements-grid {
            grid-template-columns: 1fr;
          }
          
          .action-buttons {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>

      <div className="gamification-background"></div>
      
      <div className="progress-header">
        <h2 className="progress-title">🎮 Learning Progress</h2>
        <p className="progress-subtitle">Track your journey to becoming a learning champion!</p>
      </div>

      <div className="level-section">
        <div className="level-info">
          <div className="level-badge">
            Level {currentLevel}
          </div>
          <div className="xp-info">
            <div className="xp-current">{currentXP.toLocaleString()} XP</div>
            <div className="xp-label">Experience Points</div>
          </div>
        </div>

        <div className="progress-bar-container">
          <div 
            className="progress-bar" 
            style={{ width: `${getLevelProgress()}%` }}
          ></div>
        </div>

        <div className="milestone-text">
          {milestones.find(m => m.level === currentLevel)?.title} → {milestones.find(m => m.level === currentLevel + 1)?.title || 'Max Level!'}
        </div>

        <div className="action-buttons">
          <button className="action-button xp-button" onClick={simulateXPGain}>
            🎯 Complete Quiz (+50-150 XP)
          </button>
          <button className="action-button achievement-button" onClick={unlockRandomAchievement}>
            🏆 Unlock Achievement
          </button>
        </div>
      </div>

      <div>
        <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.8rem' }}>
          🏆 Achievements Collection
        </h3>
        <div className="achievements-grid">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'} ${
                animatingBadge === achievement.id ? 'animating' : ''
              }`}
              style={{ color: getRarityColor(achievement.rarity) }}
            >
              <div 
                className="rarity-indicator" 
                style={{ backgroundColor: getRarityColor(achievement.rarity) }}
              ></div>
              <span className="achievement-icon">{achievement.icon}</span>
              <div className="achievement-name">{achievement.name}</div>
              <div className="achievement-description">{achievement.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.8rem' }}>
          📊 Learning Statistics
        </h3>
        <div className="stats-section">
          <div className="stat-card">
            <div className="stat-value">{streakCount}</div>
            <div className="stat-label">Day Streak 🔥</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{achievements.filter(a => a.unlocked).length}</div>
            <div className="stat-label">Achievements Unlocked</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{Math.floor(currentXP / 50)}</div>
            <div className="stat-label">Quizzes Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">87%</div>
            <div className="stat-label">Average Score</div>
          </div>
        </div>
      </div>

      {showLevelUp && (
        <div className="level-up-notification">
          <div className="level-up-text">🎉 LEVEL UP!</div>
          <div className="level-up-subtitle">You reached Level {currentLevel}!</div>
        </div>
      )}
    </div>
  );
};

export default ProgressGamification;